import { t } from 'i18next';
import * as yup from 'yup';

import {
  CAPACITY_LIMITS,
  ConversationPriority,
  TIME_WINDOW_SECONDS,
} from '@/shared/types/capacity-policy';

import { countWords } from '../../strings';
import {
  LENGTH_S,
  LENGTH_M,
  LENGTH_XL,
  LENGTH_XXL,
  LENGTH_1024,
  MAX_DIALOG_NAME_LENGTH,
  TAG_LENGTH,
  requireValueInRule,
} from '../constants';
import { checkForPattern, slug, tagPattern } from '../patterns';
import { urlSchema } from '../schema-builders';

export const GUIDELINE_MIN_WORDS = 3;

export const newTag = yup.lazy((value) => {
  return typeof value === 'string'
    ? yup
        .string()
        .max(TAG_LENGTH, () => t('validation.less_than', { 0: TAG_LENGTH }))
        .matches(tagPattern, { message: () => t('dialog.tags.regex_error') })
    : yup.object().nullable();
});

export const newTagSchema = yup.object({ tag: newTag });

const zendeskTag = yup
  .string()
  .matches(slug)
  .max(48, () => t('validation.less_than', { 0: 48 }))
  .required(() => t('validation.required'));

export const zendeskTagSchema = yup.object().shape({ tag: zendeskTag });

const emptyStringToUndefined = (value: number, originalValue: unknown) => {
  if (typeof originalValue === 'string' && originalValue.trim() === '') {
    return undefined;
  }
  // A cleared number input (react-hook-form `valueAsNumber`) arrives as NaN — treat it as absent
  // so a conditionally-required field validates as "empty" instead of failing min/max on NaN.
  return Number.isNaN(value) ? undefined : value;
};

const maxConversationsField = yup
  .number()
  .transform(emptyStringToUndefined)
  .min(CAPACITY_LIMITS.maxConversations.min, () =>
    t('validation.min_value', { 0: CAPACITY_LIMITS.maxConversations.min })
  )
  .max(CAPACITY_LIMITS.maxConversations.max, () =>
    t('validation.max_value', { 0: CAPACITY_LIMITS.maxConversations.max })
  );

const fairDistributionLimitField = yup
  .number()
  .transform(emptyStringToUndefined)
  .min(CAPACITY_LIMITS.fairDistributionLimit.min, () =>
    t('validation.min_value', { 0: CAPACITY_LIMITS.fairDistributionLimit.min })
  )
  .max(CAPACITY_LIMITS.fairDistributionLimit.max, () =>
    t('validation.max_value', { 0: CAPACITY_LIMITS.fairDistributionLimit.max })
  );

/** Validates the Tags card — predefined conversation tags stored on the desk. */
export const humanChatTagsSchema = yup.object({
  tag: newTag,
  tags: yup.array().of(yup.string().max(26).required()),
});

/**
 * Validates the Agent capacity card — capacity-policy fields plus per-agent overrides.
 *
 * The capacity fields share a form with the `is_service_desk` toggle but only render for service
 * desks, so they are required ONLY when that toggle is on. This lets a non-service desk (or a
 * service desk being turned OFF) still Save even if a capacity input was left blank.
 */
export const humanChatCapacitySchema = yup.object({
  max_conversations: maxConversationsField.when('is_service_desk', {
    is: true,
    then: (schema) => schema.required(() => t('validation.required')),
  }),
  fair_distribution_limit: fairDistributionLimitField.when('is_service_desk', {
    is: true,
    then: (schema) => schema.required(() => t('validation.required')),
  }),
  fair_distribution_window_seconds: yup
    .number()
    .oneOf(TIME_WINDOW_SECONDS, () => t('validation.required'))
    .when('is_service_desk', {
      is: true,
      then: (schema) => schema.required(() => t('validation.required')),
    }),
  conversation_priority: yup
    .string()
    .oneOf(Object.values(ConversationPriority), () => t('validation.required'))
    .when('is_service_desk', {
      is: true,
      then: (schema) => schema.required(() => t('validation.required')),
    }),
  overrides: yup
    .array()
    .of(
      yup.object({
        user_id: yup.string().required(),
        max_conversations: maxConversationsField.required(),
        fair_distribution_limit: fairDistributionLimitField.required(),
      })
    ),
});

/**
 * Validates the whole Human chat card under a single Save: the service-desk toggle,
 * predefined tags, the capacity-policy fields, and per-agent overrides.
 */
export const humanChatSchema = humanChatTagsSchema
  .concat(humanChatCapacitySchema)
  .concat(yup.object({ is_service_desk: yup.boolean().required() }));

/** Validates the Agent override editor (multi-select on create, single agent on edit). */
export const agentOverrideSchema = yup.object({
  user_ids: yup
    .array()
    .of(yup.string().required())
    .min(1, () => t('validation.required')),
  max_conversations: maxConversationsField.required(() => t('validation.required')),
  fair_distribution_limit: fairDistributionLimitField.required(() => t('validation.required')),
});

export const urlValidationSchema = yup.object().shape({ url: urlSchema });

export const optionSchema = yup.object().shape({
  text: yup
    .string()
    .max(LENGTH_XL, () => t('validation.less_than', { 0: LENGTH_XL }))
    .required(() => t('validation.required')),
  label: yup
    .string()
    .max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S }))
    .required(() => t('validation.required')),
});

const intent = yup
  .string()
  .min(2, () => t('validation.at_least', { 0: 2 }))
  .max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S }))
  .matches(slug);

export const intentFormSchema = yup.object().shape({
  name: yup
    .string()
    .required(() => t('validation.required'))
    .max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S })),
  intent: yup
    .object()
    .shape({
      label: yup
        .string()
        .min(2, () => t('validation.at_least', { 0: 2 }))
        .max(LENGTH_S + 1, () => t('validation.less_than', { 0: LENGTH_S + 1 })),
      value: intent,
    })
    .required(() => t('validation.required')),
  enhance: yup.boolean().required(),
});

export const conditionRuleSchema = yup.object().shape({
  name: yup
    .string()
    .matches(checkForPattern, { excludeEmptyString: true })
    .max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S }))
    .required(() => t('validation.required')),
  value: yup.string().max(LENGTH_M, () => t('validation.less_than', { 0: LENGTH_M })),
  operator: yup
    .string()
    .oneOf(['exist', 'not_exist', ...requireValueInRule])
    .required(() => t('validation.required')),
});

export const newConditionSchema = yup.object().shape({
  name: yup
    .string()
    .max(MAX_DIALOG_NAME_LENGTH, () => t('validation.less_than', { 0: MAX_DIALOG_NAME_LENGTH }))
    .required(() => t('validation.required')),
  match: yup.string().oneOf(['all', 'any', 'else', 'guideline']),
  guideline: yup.string().when('match', {
    is: 'guideline',
    then: (schema) =>
      schema
        .max(LENGTH_1024, () => t('validation.less_than', { 0: LENGTH_1024 }))
        .required(() => t('validation.required'))
        .test(
          'min-words',
          () => t('validation.at_least_words', { 0: GUIDELINE_MIN_WORDS }),
          (value) => !value || countWords(value.trim()) >= GUIDELINE_MIN_WORDS
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const askOnlySchema = yup.object().shape({
  ask_only: yup
    .string()
    .trim()
    .min(1, () => t('validation.required_field', { 0: t('dialog.email.body') }))
    .max(LENGTH_XXL, () => t('rich_text_editor.errors.char_limit')),
});

export const requisiteOptionSchema = yup.object().shape({
  text: yup
    .string()
    .max(LENGTH_XL, () => t('validation.less_than', { 0: LENGTH_XL }))
    .required(() => t('validation.required')),
  label: yup
    .string()
    .max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S }))
    .required(() => t('validation.required')),
  option_id: yup.string().required(() => t('validation.required')),
});
